import { useMutation, useQuery } from '@apollo/client';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { AppNames, PageNotFound } from '@openx/components-core/lib/PageNotFound/PageNotFound.tsx';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { UpdateBidderCreativeDocument } from '../../graphql/mutation/creatives/UpdateBidderCreative.generated.tsx';
import { GetBidderCreativeDocument } from '../../graphql/query/creatives/GetBidderCreative.generated.tsx';
import { useLocalErrorHandling } from '../graphql/useLocalErrorHandling.ts';
import { CreativeFormFields } from './CreativeForm/CreativeFormFields.tsx';
import { getChangedCreativeUpdateInput, getCreativeEditInitialState } from './CreativeForm/utils.tsx';
import type { CreativeFormState } from './CreativeForm/validation/validationSchema.ts';
import { useFetchRequiredOptions } from './hooks/useFetchRequiredOptions.ts';

export const EditCreativePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { creativeId = '' } = useParams<{ creativeId: string }>();

  const [updateBidderCreative] = useMutation(UpdateBidderCreativeDocument);

  useLocalErrorHandling(UpdateBidderCreativeDocument, () => true);

  const {
    data: creativeData,
    loading: creativeLoading,
    error: creativeError,
  } = useQuery(GetBidderCreativeDocument, {
    variables: { id: creativeId },
  });

  const {
    advertiserOptions,
    iabCategoryOptions,
    isLoading: optionsLoading,
  } = useFetchRequiredOptions({
    includeIabCategories: true,
  });

  if (creativeLoading || optionsLoading) {
    return (
      <PageHeader title={t('Edit Creative')} titlePrefix={AppNames.OpenXSelect}>
        <Loader />
      </PageHeader>
    );
  }

  if (creativeError || !creativeData?.bidderCreativeById) {
    enqueueSnackbar(t('Failed to load creative'), { variant: 'error' });

    return <PageNotFound appName={AppNames.OpenXSelect} />;
  }

  const creative = creativeData.bidderCreativeById;
  const initialValues = getCreativeEditInitialState(creative);

  const handleUpdate = async (data: CreativeFormState): Promise<void> => {
    const changedValues = getChangedCreativeUpdateInput(data, initialValues);

    const result = await updateBidderCreative({
      variables: {
        id: creativeId,
        input: changedValues,
      },
      update: cache => {
        cache.evict({ fieldName: 'bidderCreatives' });
        cache.gc();
      },
    });

    if (result.data?.updateBidderCreative) {
      enqueueSnackbar(t('Creative updated successfully'), { variant: 'success' });
      navigate(`/aqua/creatives/${creativeId}`);
    }
  };

  return (
    <PageHeader title={t('Edit Creative')} titlePrefix={AppNames.OpenXSelect}>
      <CreativeFormFields
        advertiserOptions={advertiserOptions}
        iabCategoryOptions={iabCategoryOptions}
        isEdit
        initialValues={initialValues}
        onSubmit={handleUpdate}
      />
    </PageHeader>
  );
};
